import { upsertBreakdanceBrowserParamToUrl } from "@/lib/browse-mode/index";
import { isNodeOfIframeWindowIsHtmlElement } from "@/util/dom";
import { configStore } from "@/store/store-accessor";
import { find } from "lodash";

export function injectBreakdanceBrowseModeParameters(
  iframeDocument: Document,
  iframeWindow: Window
) {
  prepareAllIframeLinksForBrowseMode(iframeDocument, iframeWindow);
  prepareAllIframeFormsForBrowseMode(iframeDocument, iframeWindow);
  prepareAllIframeLinksAndFormsForBrowseModeAgainOnceDocumentMutated(
    iframeDocument,
    iframeWindow
  );
}

function prepareAllIframeLinksForBrowseMode(
  parentNode: ParentNode,
  iframeWindow: Window
) {
  parentNode
    .querySelectorAll("a")
    .forEach(linkEl => prepareIframeLinkForBrowseMode(iframeWindow, linkEl));
}

function prepareAllIframeFormsForBrowseMode(
  parentNode: ParentNode,
  iframeWindow: Window
) {
  parentNode
    .querySelectorAll("form")
    .forEach(formEl => prepareIframeFormForBrowseMode(iframeWindow, formEl));
}

function prepareAllIframeLinksAndFormsForBrowseModeAgainOnceDocumentMutated(
  iframeDocument: Document,
  iframeWindow: Window
) {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      const targetNode = mutation.target; // Target is a node whose children changed

      if (isNodeOfIframeWindowIsHtmlElement(targetNode, iframeWindow)) {
        // Only re-inject params for the mutated parts of the document
        prepareAllIframeLinksForBrowseMode(targetNode, iframeWindow);
        prepareAllIframeFormsForBrowseMode(targetNode, iframeWindow);
      }
    });
  });

  // TODO is unobserving required?
  observer.observe(iframeDocument.documentElement, {
    childList: true,
    subtree: true
  });
}

function canUrlBeBrowsedTo(urlString: string, iframeWindow: Window): boolean {
  if (
    !urlString ||
    !urlString.length ||
    urlString.startsWith("#") ||
    urlString.startsWith("javascript:")
  ) {
    return false;
  }

  try {
    const parsedUrl = new URL(urlString, iframeWindow.location.href);

    if (!["http:", "https:"].includes(parsedUrl.protocol)) {
      return false;
    }

    const removePort = (val: string) => val.replace(/:(80|443)$/, "");

    const matchesAllowedUrl =
      undefined !==
      find(
        configStore.neededData?.browseModeAllowedUrls ?? [],
        allowedUrlString => {
          try {
            const parsedAllowedUrl = new URL(allowedUrlString);
            return (
              parsedAllowedUrl.protocol === parsedUrl.protocol &&
              removePort(parsedAllowedUrl.host) ===
                removePort(parsedUrl.host) &&
              0 ===
                parsedUrl.pathname.indexOf(
                  parsedAllowedUrl.pathname.replace(/\/$/, "")
                )
            );
          } catch (e) {
            console.error("Failed to parse browse mode allowed URL", e);
            return false;
          }
        }
      );

    if (!matchesAllowedUrl) {
      return false;
    }

    // Skip wp login and signup pages.
    if (/\/wp-(login|signup)\.php$/.test(parsedUrl.pathname)) {
      return false;
    }

    // Allow links to admin ajax as faux frontend URLs.
    if (/\/wp-admin\/admin-ajax\.php$/.test(parsedUrl.pathname)) {
      return false;
    }

    // Disallow links to admin, includes, and content.
    if (/\/wp-(admin|includes|content)(\/|$)/.test(parsedUrl.pathname)) {
      return false;
    }

    return true;
  } catch (e) {
    console.error("Browse mode: canUrlBeBrowsedTo(): invalid URL passed", e);

    return false;
  }
}

function prepareIframeLinkForBrowseMode(
  iframeWindow: Window,
  linkEl: HTMLAnchorElement
): boolean {
  if (!linkEl.hasAttribute("href")) {
    return false;
  }

  // Accessing HTMLAnchorElement.href property returns full-qualified URL
  // (with base URL prepended) instead of a raw "href" attribute value
  const hrefAttrValue = linkEl.getAttribute("href");

  if (
    hrefAttrValue === null ||
    hrefAttrValue.startsWith("#") ||
    hrefAttrValue.startsWith("javascript:")
  ) {
    return false;
  }

  if (canUrlBeBrowsedTo(hrefAttrValue, iframeWindow)) {
    linkEl.href = upsertBreakdanceBrowserParamToUrl(
      hrefAttrValue,
      iframeWindow.location.href
    );

    // Any link will be opened within the same frame
    linkEl.target = "_self";

    linkEl.classList.remove("breakdance-unbrowsable");

    return false;
  } else {
    linkEl.classList.add("breakdance-unbrowsable");
    linkEl.addEventListener("click", event => event.preventDefault());

    return true;
  }
}

function prepareIframeFormForBrowseMode(
  iframeWindow: Window,
  formEl: HTMLFormElement
): boolean {
  if (!formEl.action && iframeWindow.location.href) {
    formEl.action = iframeWindow.location.href;
  }

  if (canUrlBeBrowsedTo(formEl.action, iframeWindow)) {
    formEl.action = upsertBreakdanceBrowserParamToUrl(
      formEl.action,
      iframeWindow.location.href
    );

    // Any redirect after form submission will be bounded to the same frame
    formEl.target = "_self";

    formEl.classList.remove("breakdance-unbrowsable");

    return true;
  } else {
    formEl.classList.add("breakdance-unbrowsable");

    return false;
  }
}
